import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { MediaImageField } from "../../../components/MediaPickerDialog";
import { emptyButton, moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";
export function IndustriesSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Industries");
  const [heroHeading, setHeroHeading] = useState(
    "Solutions That Understand Your",
  );
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState("Industry");
  const [heroDescription, setHeroDescription] = useState("");
  const [heroVideo, setHeroVideo] = useState("");
  const [heroPrimary, setHeroPrimary] = useState(emptyButton());
  const [heroSecondary, setHeroSecondary] = useState(emptyButton());
  const [trustBadges, setTrustBadges] = useState([
    { icon: "Award", title: "SAP Partner", sub: "Certified Experts" },
    { icon: "Users", title: "9 Industries", sub: "Specialised Solutions" },
    { icon: "Shield", title: "150+ Projects", sub: "Across Australia" },
  ]);

  /* ---------------- WHY EXPERTISE ---------------- */
  const [whyActive, setWhyActive] = useState(true);
  const [whyBadgeText, setWhyBadgeText] = useState("Our Approach");
  const [whyHeading, setWhyHeading] = useState("Technology Alone");
  const [whyHeadingEmphasis, setWhyHeadingEmphasis] = useState("Isn't Enough");
  const [whyDescription, setWhyDescription] = useState("");
  const [whyImage, setWhyImage] = useState("");
  const [whyImageAlt, setWhyImageAlt] = useState(
    "Australian industry ERP consulting",
  );
  const [whyCTAText, setWhyCTAText] = useState("Learn About Our Approach");
  const [whyCTALink, setWhyCTALink] = useState("/about");

  /* ---------------- SECTORS ---------------- */
  const [secActive, setSecActive] = useState(true);
  const [secBadgeText, setSecBadgeText] = useState("Sectors We Serve");
  const [secHeading, setSecHeading] = useState("Sectors We");
  const [secHeadingEmphasis, setSecHeadingEmphasis] = useState("Serve");
  const [secDescription, setSecDescription] = useState("");
  const [secCards, setSecCards] = useState([
    {
      title: "",
      tagline: "",
      description: "",
      href: "",
      icon: "",
      gradient: "",
      bgColor: "",
    },
  ]);

  /* ---------------- INDUSTRY STRENGTHS ---------------- */
  const [strActive, setStrActive] = useState(true);
  const [strBadgeText, setStrBadgeText] = useState("Industry Strengths");
  const [strHeading, setStrHeading] = useState("Our Industry");
  const [strHeadingEmphasis, setStrHeadingEmphasis] = useState("Framework");
  const [strDescription, setStrDescription] = useState("");
  const [strCTAText, setStrCTAText] = useState("Meet Our Team");
  const [strCTALink, setStrCTALink] = useState("/about");
  const [strCards, setStrCards] = useState([
    { title: "", description: "", icon: "" },
  ]);

  /* ---------------- SUCCESS STORIES ---------------- */
  const [storiesActive, setStoriesActive] = useState(true);
  const [storiesBadgeText, setStoriesBadgeText] = useState("Success Stories");
  const [storiesHeading, setStoriesHeading] = useState("Transformation in");
  const [storiesHeadingEmphasis, setStoriesHeadingEmphasis] =
    useState("Action");
  const [storiesDescription, setStoriesDescription] = useState("");
  const [storiesCTAText, setStoriesCTAText] = useState("See All Case Studies");
  const [storiesCTALink, setStoriesCTALink] = useState(
    "/resources/case-studies",
  );
  const [stories, setStories] = useState([
    { quote: "", company: "", kpis: [""] },
  ]);

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState(
    "Ready to Explore Your Industry's Digital Potential?",
  );
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState(emptyButton());
  const [ctaSecondary, setCtaSecondary] = useState(emptyButton());

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
      setHeroVideo(d.hero.video || "");
      if (d.hero.primary_cta)
        setHeroPrimary({
          text: d.hero.primary_cta.text || "",
          url: d.hero.primary_cta.link || "",
          newTab: false,
        });
      if (d.hero.secondary_cta)
        setHeroSecondary({
          text: d.hero.secondary_cta.text || "",
          url: d.hero.secondary_cta.link || "",
          newTab: false,
        });
      if (d.hero.trust_badges?.length) setTrustBadges(d.hero.trust_badges);
    }

    if (d.why_expertise) {
      setWhyBadgeText(d.why_expertise.badge_text || "");
      setWhyHeading(d.why_expertise.heading || "");
      setWhyHeadingEmphasis(d.why_expertise.heading_emphasis || "");
      setWhyDescription(d.why_expertise.description || "");
      setWhyImage(d.why_expertise.image || "");
      setWhyImageAlt(d.why_expertise.image_alt || "");
      setWhyCTAText(d.why_expertise.cta_text || "");
      setWhyCTALink(d.why_expertise.cta_link || "");
    }

    if (d.sectors) {
      setSecBadgeText(d.sectors.badge_text || "");
      setSecHeading(d.sectors.heading || "");
      setSecHeadingEmphasis(d.sectors.heading_emphasis || "");
      setSecDescription(d.sectors.description || "");
      if (d.sectors.cards?.length) setSecCards(d.sectors.cards);
    }

    if (d.industry_strengths) {
      setStrBadgeText(d.industry_strengths.badge_text || "");
      setStrHeading(d.industry_strengths.heading || "");
      setStrHeadingEmphasis(d.industry_strengths.heading_emphasis || "");
      setStrDescription(d.industry_strengths.description || "");
      setStrCTAText(d.industry_strengths.cta_text || "");
      setStrCTALink(d.industry_strengths.cta_link || "");
      if (d.industry_strengths.cards?.length)
        setStrCards(d.industry_strengths.cards);
    }

    if (d.success_stories) {
      setStoriesBadgeText(d.success_stories.badge_text || "");
      setStoriesHeading(d.success_stories.heading || "");
      setStoriesHeadingEmphasis(d.success_stories.heading_emphasis || "");
      setStoriesDescription(d.success_stories.description || "");
      setStoriesCTAText(d.success_stories.cta_text || "");
      setStoriesCTALink(d.success_stories.cta_link || "");
      if (d.success_stories.stories?.length)
        setStories(d.success_stories.stories);
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaDescription(d.final_cta.description || "");
      if (d.final_cta.primary_cta)
        setCtaPrimary({
          text: d.final_cta.primary_cta.text || "",
          url: d.final_cta.primary_cta.link || "",
          newTab: false,
        });
      if (d.final_cta.secondary_cta)
        setCtaSecondary({
          text: d.final_cta.secondary_cta.text || "",
          url: d.final_cta.secondary_cta.link || "",
          newTab: false,
        });
    }
  }, [initialData]);

  const buildPayload = () => ({
    hero: {
      badge_text: heroBadgeText,
      heading: heroHeading,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
      video: heroVideo,
      primary_cta: { text: heroPrimary.text, link: heroPrimary.url },
      secondary_cta: { text: heroSecondary.text, link: heroSecondary.url },
      trust_badges: trustBadges,
    },
    why_expertise: {
      badge_text: whyBadgeText,
      heading: whyHeading,
      heading_emphasis: whyHeadingEmphasis,
      description: whyDescription,
      image: whyImage,
      image_alt: whyImageAlt,
      cta_text: whyCTAText,
      cta_link: whyCTALink,
    },
    sectors: {
      badge_text: secBadgeText,
      heading: secHeading,
      heading_emphasis: secHeadingEmphasis,
      description: secDescription,
      cards: secCards,
    },
    industry_strengths: {
      badge_text: strBadgeText,
      heading: strHeading,
      heading_emphasis: strHeadingEmphasis,
      description: strDescription,
      cta_text: strCTAText,
      cta_link: strCTALink,
      cards: strCards,
    },
    success_stories: {
      badge_text: storiesBadgeText,
      heading: storiesHeading,
      heading_emphasis: storiesHeadingEmphasis,
      description: storiesDescription,
      cta_text: storiesCTAText,
      cta_link: storiesCTALink,
      stories: stories,
    },
    final_cta: {
      heading: ctaHeading,
      description: ctaDescription,
      primary_cta: { text: ctaPrimary.text, link: ctaPrimary.url },
      secondary_cta: { text: ctaSecondary.text, link: ctaSecondary.url },
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    heroVideo,
    heroPrimary,
    heroSecondary,
    trustBadges,
    whyBadgeText,
    whyHeading,
    whyHeadingEmphasis,
    whyDescription,
    whyImage,
    whyImageAlt,
    whyCTAText,
    whyCTALink,
    secBadgeText,
    secHeading,
    secHeadingEmphasis,
    secDescription,
    secCards,
    strBadgeText,
    strHeading,
    strHeadingEmphasis,
    strDescription,
    strCTAText,
    strCTALink,
    strCards,
    storiesBadgeText,
    storiesHeading,
    storiesHeadingEmphasis,
    storiesDescription,
    storiesCTAText,
    storiesCTALink,
    stories,
    ctaHeading,
    ctaDescription,
    ctaPrimary,
    ctaSecondary,
  ]);

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
        <Field label="Hero Video">
          <MediaImageField
            label="Select Hero Video"
            value={heroVideo}
            onChange={setHeroVideo}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={heroPrimary.text}
          url={heroPrimary.url}
          newTab={heroPrimary.newTab}
          onTextChange={(v) => updateBtn(setHeroPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={heroSecondary.text}
          url={heroSecondary.url}
          newTab={heroSecondary.newTab}
          onTextChange={(v) => updateBtn(setHeroSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroSecondary, "newTab", v)}
        />

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Trust Badges</p>
          <button
            onClick={() =>
              setTrustBadges((s) => [...s, { icon: "", title: "", sub: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Badge
          </button>
        </div>
        {trustBadges.map((b, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Badge {i + 1}</p>
              {trustBadges.length > 1 && (
                <button
                  onClick={() =>
                    setTrustBadges((s) => s.filter((_, idx) => idx !== i))
                  }
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <Field label="Icon">
              <SelectInput
                value={b.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setTrustBadges((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title">
                <TextInput
                  value={b.title}
                  onChange={(e) =>
                    setTrustBadges((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Sub">
                <TextInput
                  value={b.sub}
                  onChange={(e) =>
                    setTrustBadges((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, sub: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* WHY EXPERTISE */}
      <CollapsibleSection
        title="Why Industry Expertise"
        active={whyActive}
        onToggleActive={setWhyActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={whyBadgeText}
            onChange={(e) => setWhyBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={whyHeading}
            onChange={(e) => setWhyHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={whyHeadingEmphasis}
            onChange={(e) => setWhyHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={whyDescription}
            onChange={(e) => setWhyDescription(e.target.value)}
          />
        </Field>
        <Field label="Section Image">
          <MediaImageField
            label="Select Image"
            value={whyImage}
            onChange={setWhyImage}
          />
        </Field>
        <Field label="Image Alt Text">
          <TextInput
            value={whyImageAlt}
            onChange={(e) => setWhyImageAlt(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={whyCTAText}
              onChange={(e) => setWhyCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={whyCTALink}
              onChange={(e) => setWhyCTALink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* SECTORS */}
      <CollapsibleSection
        title="Sectors We Serve"
        active={secActive}
        onToggleActive={setSecActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={secBadgeText}
            onChange={(e) => setSecBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={secHeading}
            onChange={(e) => setSecHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={secHeadingEmphasis}
            onChange={(e) => setSecHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={secDescription}
            onChange={(e) => setSecDescription(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Sector Cards</p>
          <button
            onClick={() =>
              setSecCards((s) => [
                ...s,
                {
                  title: "",
                  tagline: "",
                  description: "",
                  href: "",
                  icon: "",
                  gradient: "",
                  bgColor: "",
                },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Sector
          </button>
        </div>
        {secCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Sector {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={secCards.length}
                  onMove={(f, t) => setSecCards(moveItem(secCards, f, t))}
                />
                {secCards.length > 1 && (
                  <button
                    onClick={() =>
                      setSecCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setSecCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Tagline">
              <TextInput
                value={c.tagline}
                onChange={(e) =>
                  setSecCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, tagline: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setSecCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Link (href)">
              <TextInput
                value={c.href}
                onChange={(e) =>
                  setSecCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, href: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setSecCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Gradient (Tailwind classes)">
              <TextInput
                value={c.gradient}
                placeholder="from-orange-400 via-amber-500 to-yellow-400"
                onChange={(e) =>
                  setSecCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, gradient: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Background Color (Tailwind classes)">
              <TextInput
                value={c.bgColor}
                placeholder="bg-gradient-to-br from-orange-50 to-amber-50"
                onChange={(e) =>
                  setSecCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, bgColor: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* INDUSTRY STRENGTHS */}
      <CollapsibleSection
        title="Industry Strengths"
        active={strActive}
        onToggleActive={setStrActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={strBadgeText}
            onChange={(e) => setStrBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={strHeading}
            onChange={(e) => setStrHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={strHeadingEmphasis}
            onChange={(e) => setStrHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={strDescription}
            onChange={(e) => setStrDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={strCTAText}
              onChange={(e) => setStrCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={strCTALink}
              onChange={(e) => setStrCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Strength Cards</p>
          <button
            onClick={() =>
              setStrCards((s) => [
                ...s,
                { title: "", description: "", icon: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {strCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={strCards.length}
                  onMove={(f, t) => setStrCards(moveItem(strCards, f, t))}
                />
                {strCards.length > 1 && (
                  <button
                    onClick={() =>
                      setStrCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setStrCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setStrCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setStrCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* SUCCESS STORIES */}
      <CollapsibleSection
        title="Success Stories"
        active={storiesActive}
        onToggleActive={setStoriesActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={storiesBadgeText}
            onChange={(e) => setStoriesBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={storiesHeading}
            onChange={(e) => setStoriesHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={storiesHeadingEmphasis}
            onChange={(e) => setStoriesHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={storiesDescription}
            onChange={(e) => setStoriesDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={storiesCTAText}
              onChange={(e) => setStoriesCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={storiesCTALink}
              onChange={(e) => setStoriesCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Stories</p>
          <button
            onClick={() =>
              setStories((s) => [...s, { quote: "", company: "", kpis: [""] }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Story
          </button>
        </div>
        {stories.map((story, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Story {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={stories.length}
                  onMove={(f, t) => setStories(moveItem(stories, f, t))}
                />
                {stories.length > 1 && (
                  <button
                    onClick={() =>
                      setStories((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Quote">
              <TextArea
                value={story.quote}
                onChange={(e) =>
                  setStories((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, quote: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Company">
              <TextInput
                value={story.company}
                onChange={(e) =>
                  setStories((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, company: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            {/* KPIs */}
            <div className="flex justify-between mb-1">
              <p className="text-xs font-semibold">KPIs</p>
              <button
                onClick={() =>
                  setStories((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, kpis: [...x.kpis, ""] } : x,
                    ),
                  )
                }
                className="flex items-center gap-1 text-xs"
              >
                <Plus size={12} /> Add KPI
              </button>
            </div>
            {story.kpis?.map((kpi, ki) => (
              <div key={ki} className="flex gap-2 items-center">
                <TextInput
                  value={kpi}
                  placeholder="e.g. 40% faster dispatch"
                  onChange={(e) =>
                    setStories((s) =>
                      s.map((x, idx) =>
                        idx === i
                          ? {
                              ...x,
                              kpis: x.kpis.map((k, kidx) =>
                                kidx === ki ? e.target.value : k,
                              ),
                            }
                          : x,
                      ),
                    )
                  }
                />
                {story.kpis.length > 1 && (
                  <button
                    onClick={() =>
                      setStories((s) =>
                        s.map((x, idx) =>
                          idx === i
                            ? {
                                ...x,
                                kpis: x.kpis.filter((_, kidx) => kidx !== ki),
                              }
                            : x,
                        ),
                      )
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        ))}
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection
        title="Final CTA"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDescription}
            onChange={(e) => setCtaDescription(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          newTab={ctaSecondary.newTab}
          onTextChange={(v) => updateBtn(setCtaSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaSecondary, "newTab", v)}
        />
      </CollapsibleSection>
    </>
  );
}
